import "./index.scss";
import React, { FunctionComponent, useState, useEffect } from "react";
import template from "lodash.template";
import { isUrlValid } from "../../shared/validations"

import { Invocations, SelectedWidget } from "../../containers/Wizard";
import ButtonOptions from "../ButtonOptions";
import OrigamiPreview from "../OrigamiPreview";
import { Tab } from "@own-local/ad-unit/lib/AdUnit";
import SchedulingOptions from "../SchedulingOptions";
import { Spinner } from "../Spinner";

export const ORIGAMI_DATA_SPELLS = [
  "business",
  "business_name",
  "caption",
  "background",
  "logo",
  "layout",
  "address",
  "city",
  "zip",
  "business_phone",
  "website",
  "facebook",
  "twitter",
  "instagram",
  "additional_text",
  "email",
  "state",
];

const ORIGAMI_DATA_PREPENDS_MAP: OrigamiDataType = {
  facebook: "https://facebook.com/",
  twitter: "https://twitter.com/",
  instagram: "https://instagram.com/",
};

export type BusinessDataType = {
  uuid: string;
  name: string;
};

export type OrigamiDataType = {
  [key: string]: string | object | undefined;
};

type FillOrigamiSchedulingStepProps = {
  previous?: string;
  next: string;
  question: string;
  target?: string;
  spellName: string;
  invocations: Invocations;
  description?: string;
  selectedTab?: Tab;
  showExpanded?: boolean;
  options: Array<any>;
  loading?: boolean;
  invocate: Function;
  getWidgets?: Function;
  changePath: Function;
};

const defineOrigamiData = (invocations: Invocations): OrigamiDataType => {
  return ORIGAMI_DATA_SPELLS.reduce<OrigamiDataType>(
    (origamiData, spell): OrigamiDataType => {
      const prependValue = ORIGAMI_DATA_PREPENDS_MAP[spell] || "";
      if (invocations[spell]) origamiData[spell] = `${prependValue}${invocations[spell]}`;

      if (spell === "logo") {
        origamiData["logo"] = isUrlValid(invocations["logo"]?.toString()) ? invocations["logo"] : ""
      }

      return origamiData;
    },
    {},
  );
};

export const FillOrigamiSchedulingStep: FunctionComponent<FillOrigamiSchedulingStepProps> = ({
  previous,
  next,
  question,
  target,
  spellName,
  invocations,
  description,
  showExpanded,
  options,
  invocate,
  changePath,
  getWidgets,
  loading,
}: FillOrigamiSchedulingStepProps) => {
  const [origamiData, setOrigamiData] = useState<OrigamiDataType>(
    defineOrigamiData(invocations),
  );
  let spellValue = invocations[spellName];
  if (spellValue && spellName === "widget") {
    spellValue = spellValue as SelectedWidget;
    spellValue = spellValue.uuid;
  }
  const initialValue = String(spellValue || "");
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue, setValue]);

  useEffect(() => {
    setOrigamiData(defineOrigamiData(invocations));
  }, [setOrigamiData, invocations]);

  useEffect(() => {
    if (getWidgets && options.length === 0) {
      getWidgets();
    }
  }, [getWidgets, options]);

  const handleOptionSelection = (val: string) => {
    setValue(val);
  };

  const handleSubmit = () => {
    if (initialValue === value) changePath(next);
    else invocate(spellName, value, next);
  };

  const renderQuestion = () => {
    const temp = template(question);
    const targetHtml = target ? `<strong>${target}</strong>` : "";
    const innerHtml = `${temp({ target: targetHtml })}?`;

    return {
      __html: innerHtml,
    };
  };

  return (
    <div>
      <OrigamiPreview
        spell={spellName}
        origamiData={origamiData}
        selectedTab={Tab.Info}
        showExpanded={showExpanded}
      />
      <div className="question">
        <h3 dangerouslySetInnerHTML={renderQuestion()} />
        {description && <h4>{description}</h4>}
        {loading ?
          <div className="step">
            <Spinner />
          </div>
          :
          <SchedulingOptions
            options={options}
            handleOptionSelection={handleOptionSelection}
            spell={spellName}
            selectedOption={value}
          />
        }
        <ButtonOptions
          changePath={changePath}
          previous={previous}
          handleSubmit={handleSubmit}
        />
      </div>
    </div>
  );
};

export default FillOrigamiSchedulingStep;
